import domainFn from './defence';
import env from './env';

/**
 * 996hezi 主域下直连 96show 的 httpApi 相对路径白名单（config 仍写 /payApi、/authApi、/pcApi/...）。
 * 新增迁移：在对应分组 paths 里加一行完整路径即可，不必改 auth.ts / home.ts 等。
 */
type HeziDirectApiGroup = {
  paths: Set<string>;
  prefix: RegExp;
  getDomain: () => string;
};

const HEZI_DIRECT_API_GROUPS: HeziDirectApiGroup[] = [
  {
    paths: new Set([
      '/payApi/v1/Pay/QrAlipayBox',
      '/payApi/v1/Pay/QrWechatBox',
      '/payApi/v1/PayStatus/Check',
      '/payApi/v1/RoleInfo/BoxPayGrade'
    ]),
    prefix: /^\/payApi/,
    getDomain: () => env.showPayApiDomain
  },
  {
    paths: new Set(['/authApi/user/v1/agreement/fixedUrlByCode', '/authApi/tip/v1/floatPopupList']),
    prefix: /^\/authApi/,
    getDomain: () => env.showApiDomain
  },
  {
    paths: new Set(['/pcApi/v1/homeapi/Homeindex/defaultAgreement']),
    prefix: /^\/pcApi/,
    getDomain: () => env.showPcApiDomain
  }
];

/** 相对路径：/authApi 下的 96秀业务（voice / show / redbag） */
const SHOW_AUTH_API_PATTERN = /^\/authApi\/(voice|show|redbag)\//;
/** 绝对路径：历史 hardcode 的 996hezi + voice|show|redbag */
const HEZI_SHOW_API_PATH_PATTERN = /\.996hezi\.com\/(voice|show|redbag)\//i;
/** 绝对路径：历史 hardcode 的 live-api*.996hezi.com */
const HEZI_LIVE_API_HOST_PATTERN = /^(https?:)?\/\/(live-api(?:-pre|-dev)?)\.996hezi\.com(\/|$)/i;

function isHeziDomain(): boolean {
  return domainFn.domain === '996hezi.com';
}

/** 兼容配置里写死的 996hezi 完整 URL → 96show.com */
function mapLegacyHeziHost(path: string): string {
  const liveMatch = path.match(HEZI_LIVE_API_HOST_PATTERN);
  if (liveMatch) {
    const protocol = liveMatch[1];
    const stageHost = liveMatch[2];
    const targetPrefix = protocol ? `${protocol}//${stageHost}.96show.com` : `//${stageHost}.96show.com`;
    return path.replace(/^(https?:)?\/\/[^/]+/i, targetPrefix);
  }
  if (HEZI_SHOW_API_PATH_PATTERN.test(path)) {
    return path.replace(/\.996hezi\.com(?=\/(?:voice|show|redbag)\/)/i, '.96show.com');
  }
  return path;
}

/** 996hezi：白名单路径 → 对应 96show 域名（按分组替换 /payApi、/authApi、/pcApi 前缀） */
function mapHeziDirectApi(path: string): string | null {
  if (!isHeziDomain()) {
    return null;
  }
  for (const group of HEZI_DIRECT_API_GROUPS) {
    if (group.paths.has(path)) {
      return path.replace(group.prefix, group.getDomain());
    }
  }
  return null;
}

/** 996hezi：/authApi/(voice|show|redbag)/ → env.showApiDomain（如 //test-api.96show.com） */
function mapShowAuthApi(path: string): string | null {
  if (!isHeziDomain() || !SHOW_AUTH_API_PATTERN.test(path)) {
    return null;
  }
  return path.replace('/authApi', env.showApiDomain);
}

function applyProxyPrefix(path: string): string {
  let resolved = path;
  env.proxyCurrent.forEach((item: { prefix: string; target: string }) => {
    if (resolved.includes(item.prefix)) {
      resolved = resolved.replace(item.prefix, item.target);
    }
  });
  return resolved;
}

/**
 * 将 httpApi 配置中的路径解析为最终请求地址（factory 唯一入口）。
 *
 * 996hezi 主域规则：
 * - HEZI_DIRECT_API_GROUPS 白名单 → showPayApiDomain / showApiDomain / showPcApiDomain
 * - /authApi/(voice|show|redbag)/* → showApiDomain（正则，非白名单）
 * - /liveApi/* → live-api(-dev|-pre).96show.com（由 env.proxyCurrent 目标决定）
 * - 其它 /authApi/*、/pcApi/* 等 → test-api/pre-api/api.996hezi.com、pc-api*.996hezi.com（不变）
 *
 * 996box 等其它主域：白名单不生效，走 proxy；不做 96show 切换。
 */
export function resolveApiUrl(path: string): string {
  const heziDirect = mapHeziDirectApi(path);
  if (heziDirect !== null) {
    return heziDirect;
  }
  const showAuth = mapShowAuthApi(path);
  if (showAuth !== null) {
    return showAuth;
  }
  const proxied = applyProxyPrefix(path);
  return mapLegacyHeziHost(proxied);
}
